import React, { useEffect, useMemo, useState } from "react";

/** Utils */
function parseNum(v) {
  const s = String(v ?? "")
    .trim()
    .replace(/\s/g, "")
    .replace(",", ".");
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

function clamp(n, min, max) {
  if (!Number.isFinite(n)) return min;
  return Math.min(Math.max(n, min), max);
}

function formatEUR(n) {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
  }).format(n);
}

function formatInt(n) {
  return new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(n);
}

function calcMonthlyPayment(principal, annualRatePct, months) {
  const P = principal;
  const n = months;
  const r = annualRatePct / 100 / 12;

  if (P <= 0 || n <= 0) return 0;
  if (r === 0) return P / n;

  const denom = 1 - Math.pow(1 + r, -n);
  return (P * r) / denom;
}

/** Assurance "standard" sur capital initial (mensualité fixe) */
function calcInsuranceMonthly(principal, annualInsurancePct) {
  const P = principal;
  const t = annualInsurancePct / 100;
  if (P <= 0 || t <= 0) return 0;
  return (P * t) / 12;
}

/** Barème taux auto (ajuste si besoin) */
const DEFAULT_RATE_TABLE = [
  { years: 10, rate: 3.3 },
  { years: 15, rate: 3.45 },
  { years: 20, rate: 3.5 },
  { years: 25, rate: 3.61 },
  { years: 30, rate: 3.75 },
];

function interpolateRate(years, table) {
  const sorted = [...table].sort((a, b) => a.years - b.years);
  if (!sorted.length) return 0;

  if (years <= sorted[0].years) return sorted[0].rate;
  if (years >= sorted[sorted.length - 1].years)
    return sorted[sorted.length - 1].rate;

  for (let i = 0; i < sorted.length - 1; i++) {
    const a = sorted[i];
    const b = sorted[i + 1];
    if (years >= a.years && years <= b.years) {
      const t = (years - a.years) / (b.years - a.years);
      return a.rate + t * (b.rate - a.rate);
    }
  }
  return sorted[0].rate;
}

export default function CalculMensualites() {
  // Inputs (string)
  const [amount, setAmount] = useState("150000");
  const [years, setYears] = useState("20");
  const [useAutoRate, setUseAutoRate] = useState(true);
  const [annualRate, setAnnualRate] = useState("3,50");
  const [insuranceRate, setInsuranceRate] = useState("0,34");

  // Bornes curseurs
  const AMOUNT_MIN = 20000;
  const AMOUNT_MAX = 1000000;
  const AMOUNT_STEP = 1000;

  const YEARS_MIN = 5;
  const YEARS_MAX = 30;
  const YEARS_STEP = 1;

  const computed = useMemo(() => {
    const P = clamp(parseNum(amount), AMOUNT_MIN, 50_000_000);
    const y = clamp(parseNum(years), YEARS_MIN, 40);
    const months = Math.round(y * 12);

    const auto = interpolateRate(y, DEFAULT_RATE_TABLE);
    const rateUsed = useAutoRate ? auto : clamp(parseNum(annualRate), 0, 30);
    const insRate = clamp(parseNum(insuranceRate), 0, 5);

    const monthlyNoIns = calcMonthlyPayment(P, rateUsed, months);
    const insMonthly = calcInsuranceMonthly(P, insRate);
    const monthlyTotal = monthlyNoIns + insMonthly;

    const totalPaidNoIns = monthlyNoIns * months;
    const totalInterest = totalPaidNoIns - P;

    const totalIns = insMonthly * months;
    const totalPaidWithIns = totalPaidNoIns + totalIns;

    return {
      P,
      y,
      months,
      autoRate: auto,
      rateUsed,
      insRate,
      monthlyNoIns,
      insMonthly,
      monthlyTotal,
      totalInterest,
      totalPaidNoIns,
      totalIns,
      totalPaidWithIns,
    };
  }, [amount, years, annualRate, insuranceRate, useAutoRate]);

  const canCompute = computed.P > 0 && computed.months > 0;

  // Sync champ taux quand auto
  useEffect(() => {
    if (useAutoRate) {
      setAnnualRate(String(computed.autoRate.toFixed(2)).replace(".", ","));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useAutoRate, computed.autoRate]);

  // Helpers pour sliders
  const amountNum = clamp(parseNum(amount), AMOUNT_MIN, AMOUNT_MAX);
  const yearsNum = clamp(parseNum(years), YEARS_MIN, YEARS_MAX);

  return (
    <div className="page">
      <div className="hero">
        <h1>Calcul des mensualités</h1>
        <p className="muted" style={{ margin: 0 }}>
          Mensualité de crédit amortissable + assurance « standard » (sur
          capital initial). Calcul 100% local.
        </p>
      </div>

      <div style={{ height: 16 }} />

      <div className="grid-2">
        {/* Entrées */}
        <div className="card">
          <h2 className="cardTitle">Entrées</h2>

          {/* Montant + curseur */}
          <label className="label">
            Montant emprunté (€)
            <input
              className="input"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputMode="decimal"
              placeholder="Ex: 150000"
            />
            <input
              type="range"
              min={AMOUNT_MIN}
              max={AMOUNT_MAX}
              step={AMOUNT_STEP}
              value={amountNum}
              onChange={(e) => setAmount(String(e.target.value))}
              style={{ width: "100%", marginTop: 10 }}
              aria-label="Curseur montant emprunté"
            />
            <div className="small muted" style={{ marginTop: 6 }}>
              {formatInt(AMOUNT_MIN)} € — {formatInt(AMOUNT_MAX)} € • Valeur :{" "}
              <strong>{formatInt(amountNum)} €</strong>
            </div>
          </label>

          {/* Durée + curseur */}
          <label className="label">
            Durée (années)
            <input
              className="input"
              value={years}
              onChange={(e) => setYears(e.target.value)}
              inputMode="numeric"
              placeholder="Ex: 20"
            />
            <input
              type="range"
              min={YEARS_MIN}
              max={YEARS_MAX}
              step={YEARS_STEP}
              value={yearsNum}
              onChange={(e) => setYears(String(e.target.value))}
              style={{ width: "100%", marginTop: 10 }}
              aria-label="Curseur durée en années"
            />
            <div className="small muted" style={{ marginTop: 6 }}>
              {YEARS_MIN} ans — {YEARS_MAX} ans • Valeur :{" "}
              <strong>{yearsNum} ans</strong> ({computed.months} mois)
            </div>
          </label>

          <div className="grid-2">
            <label className="label">
              Mode taux
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button
                  type="button"
                  className={`btn ${useAutoRate ? "" : "btnSecondary"}`}
                  onClick={() => setUseAutoRate(true)}
                >
                  Auto
                </button>
                <button
                  type="button"
                  className={`btn ${!useAutoRate ? "" : "btnSecondary"}`}
                  onClick={() => setUseAutoRate(false)}
                >
                  Manuel
                </button>
              </div>
              <div className="small" style={{ marginTop: 8 }}>
                {useAutoRate ? (
                  <>
                    Taux estimé selon la durée :{" "}
                    <strong>{computed.autoRate.toFixed(2)} %</strong>
                  </>
                ) : (
                  <>Saisis ton taux avant négociation.</>
                )}
              </div>
            </label>

            <label className="label">
              Taux annuel (%)
              <input
                className="input"
                value={annualRate}
                onChange={(e) => setAnnualRate(e.target.value)}
                inputMode="decimal"
                disabled={useAutoRate}
                style={
                  useAutoRate
                    ? { opacity: 0.7, cursor: "not-allowed" }
                    : undefined
                }
              />
            </label>
          </div>

          <label className="label">
            Taux d’assurance annuel (%) (standard)
            <input
              className="input"
              value={insuranceRate}
              onChange={(e) => setInsuranceRate(e.target.value)}
              inputMode="decimal"
              placeholder="Ex: 0,34"
            />
          </label>

          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              marginTop: 14,
            }}
          >
            <button
              className="btn btnSecondary"
              onClick={() => {
                setAmount("150000");
                setYears("20");
                setUseAutoRate(true);
                setAnnualRate("3,50");
                setInsuranceRate("0,34");
              }}
            >
              Réinitialiser
            </button>
          </div>

          <div className="small" style={{ marginTop: 10 }}>
            NB : l’assurance peut être calculée différemment selon les contrats
            (capital restant dû, etc.).
          </div>
        </div>

        {/* Résultats */}
        <div className="card">
          <h2 className="cardTitle">Résultats</h2>

          {!canCompute ? (
            <p className="muted">Renseigne un montant et une durée.</p>
          ) : (
            <>
              <ResultRow
                label="Mensualité (assurance incluse)"
                value={formatEUR(computed.monthlyTotal)}
              />
              <ResultRow
                label="Mensualité hors assurance"
                value={formatEUR(computed.monthlyNoIns)}
              />
              <ResultRow
                label="Assurance / mois"
                value={formatEUR(computed.insMonthly)}
              />

              <div style={{ height: 14 }} />

              <h3
                style={{
                  margin: "0 0 10px 0",
                  fontSize: 16,
                  letterSpacing: ".2px",
                }}
              >
                Détails
              </h3>

              <div
                className="card"
                style={{ padding: 12, background: "rgba(255,255,255,.06)" }}
              >
                <SmallLine
                  label="Capital emprunté"
                  value={formatEUR(computed.P)}
                />
                <SmallLine
                  label="Durée"
                  value={`${computed.y} ans (${computed.months} mois)`}
                />
                <SmallLine
                  label="Taux nominal utilisé"
                  value={`${computed.rateUsed.toFixed(2)} %`}
                />
                <SmallLine
                  label="Taux assurance"
                  value={`${computed.insRate.toFixed(2)} %`}
                />
                <SmallLine
                  label="Intérêts estimés (hors assurance)"
                  value={formatEUR(computed.totalInterest)}
                />
                <SmallLine
                  label="Coût total crédit (hors assurance)"
                  value={formatEUR(computed.totalPaidNoIns)}
                />
                <SmallLine
                  label="Coût total assurance"
                  value={formatEUR(computed.totalIns)}
                />
                <SmallLine
                  label="Coût total (avec assurance)"
                  value={formatEUR(computed.totalPaidWithIns)}
                />
              </div>

              <div className="small" style={{ marginTop: 10 }}>
                Mensualité calculée sur prêt amortissable (mensualités
                constantes). Assurance « standard » sur capital initial.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ResultRow({ label, value }) {
  return (
    <div
      className="resultRow"
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        padding: "10px 0",
        borderBottom: "1px solid rgba(255,255,255,.10)",
      }}
    >
      <span className="muted">{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function SmallLine({ label, value }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 10,
        padding: "6px 0",
      }}
    >
      <span className="muted" style={{ fontSize: 13 }}>
        {label}
      </span>
      <strong style={{ fontSize: 13 }}>{value}</strong>
    </div>
  );
}
